import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { FaApple, FaAndroid } from "react-icons/fa";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import useDarkMode from "use-dark-mode";
import Card from "../../components/Card";
import Avatar from "../../components/Dashboard/Avatar";
import Chip from "../../components/Dashboard/Chip";
import KpiTile, { KpiGrid } from "../../components/Dashboard/KpiTile";
import TabsHeader, { useTabParam } from "../../components/Dashboard/TabsHeader";
import Timeline from "../../components/Dashboard/Timeline";
import Modal from "../../components/Modal";
import { getSubscriptionsStats } from "../../api/stats.service";
import { PLAN_EUR, rowPrice, chargeEur, ltvOf } from "./subMoney";
import styles from "./Subscriptions.module.sass";

/**
 * Suscripciones, al estilo RevenueCat / App Store Connect:
 *   Resumen        KPIs con variación y sparkline; el KPI elegido pinta el
 *                  gráfico grande; actividad, estados y top clientes
 *   Próximos       agenda de renovaciones, fines de prueba y bajas
 *   Transacciones  el libro de cobros: cualquier periodo, cobro a cobro
 *   Clientes       tabla ordenable con LTV, partidas y exportación
 *
 * Pinchar cualquier número o barra abre un modal con las personas; pinchar una
 * persona abre su ficha con el timeline completo. Los datos vienen del backend
 * (GET /admin/stats/subscriptions): filas clasificadas, cobros reconstruidos
 * (`payments`), precio real de Apple (`realPrice`) y partidas por usuario.
 * Las cuentas internas (admins) ya vienen excluidas por la API.
 */

const STATE = {
  paying: { label: "Activa", color: "#4E9A3A", bg: "rgba(131,191,110,.18)" },
  paying_cancelled: { label: "No renovará", color: "#B78A00", bg: "rgba(255,216,141,.3)" },
  trial: { label: "En prueba", color: "#8E59FF", bg: "rgba(142,89,255,.15)" },
  trial_cancelled: { label: "Prueba cancelada", color: "#C45FA0", bg: "rgba(255,106,194,.15)" },
  grace: { label: "Cobro fallido", color: "#E2562C", bg: "rgba(255,106,85,.15)" },
  lifetime: { label: "Lifetime", color: "#2A85FF", bg: "rgba(42,133,255,.15)" },
  ended: { label: "Baja", color: "#6F767E", bg: "rgba(111,118,126,.15)" },
};
const PLAN = { monthly: "Mensual", yearly: "Anual", lifetime: "Lifetime" };
const PLAN_COLOR = { monthly: "#83BF6E", yearly: "#2A85FF", lifetime: "#FFB020" };



const KIND = {
  first: { label: "Primer pago", color: "#4E9A3A", bg: "rgba(131,191,110,.18)" },
  renewal: { label: "Renovación", color: "#2A85FF", bg: "rgba(42,133,255,.15)" },
  lifetime: { label: "Lifetime", color: "#B78A00", bg: "rgba(255,216,141,.3)" },
};
const EVENT = {
  first: { label: "Primer pago", color: "#4E9A3A", bg: "rgba(131,191,110,.18)" },
  renewal: { label: "Renovación", color: "#2A85FF", bg: "rgba(42,133,255,.15)" },
  lifetime: { label: "Compra lifetime", color: "#B78A00", bg: "rgba(255,216,141,.3)" },
  trial_start: { label: "Prueba iniciada", color: "#8E59FF", bg: "rgba(142,89,255,.15)" },
  cancel: { label: "Cancelación", color: "#E2562C", bg: "rgba(255,106,85,.15)" },
  end: { label: "Pierde el acceso", color: "#6F767E", bg: "rgba(111,118,126,.15)" },
};

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const n = (v) => (v == null ? "—" : Number(v).toLocaleString("es-ES"));
const eur = (v) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const day = (d) => (d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "—");
const dayShort = (d) => (d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" }) : "—");
const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthStart = (y, m) => new Date(y, m, 1);
const monthLabel = (d) => `${MES_CORTO[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
const calDay = (d) => {
  const x = new Date(d);
  return Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()) / 864e5;
};
const daysUntil = (d) => calDay(d) - calDay(new Date());
const daysAgo = (d) => -daysUntil(d);
const relPast = (d) => (daysAgo(d) === 0 ? "hoy" : daysAgo(d) === 1 ? "ayer" : `hace ${daysAgo(d)} días`);
const relDay = (d) => {
  const x = daysUntil(d);
  return x < 0 ? "ayer" : x === 0 ? "hoy" : x === 1 ? "mañana" : `en ${x} días`;
};

const PlatformIcon = ({ platform, size = 14 }) =>
  platform === "APPLE" ? <FaApple size={size} title="iOS" /> : <FaAndroid size={size} title="Android" color="#3DDC84" />;

/** Los 5 grupos de gente. `date` es la fecha que manda en su lista. */
const SEGMENTS = [
  {
    key: "paying",
    title: "De pago",
    states: ["paying", "paying_cancelled"],
    color: "#83BF6E",
    date: (r) => r.firstPaymentAt,
    dateLabel: "Cliente desde",
    when: "Primer pago de cada cliente",
    extra: [
      { key: "renew", label: "Renuevan", test: (r) => r.state === "paying" },
      { key: "norenew", label: "No renovarán", test: (r) => r.state === "paying_cancelled" },
    ],
  },
  {
    key: "trial",
    title: "En prueba",
    states: ["trial", "trial_cancelled"],
    color: "#8E59FF",
    date: (r) => r.activatedAt,
    dateLabel: "Inicio de la prueba",
    when: "Inicio de cada prueba",
    extra: [
      { key: "willpay", label: "Sin cancelar", test: (r) => r.state === "trial" },
      { key: "cancelled", label: "Ya cancelaron", test: (r) => r.state === "trial_cancelled" },
    ],
  },
  {
    key: "grace",
    title: "Cobro fallido",
    states: ["grace"],
    color: "#FF6A55",
    date: (r) => r.activatedAt,
    dateLabel: "Alta",
    when: "Fecha de alta",
    explain: "La tienda reintenta el cobro; mantienen el acceso mientras tanto.",
    extra: [],
  },
  {
    key: "lifetime",
    title: "Lifetime",
    states: ["lifetime"],
    color: "#2A85FF",
    date: (r) => r.activatedAt,
    dateLabel: "Compra",
    when: "Fecha de compra",
    extra: [],
  },
  {
    key: "ended",
    title: "Bajas",
    states: ["ended"],
    color: "#9A9FA5",
    date: (r) => r.cancelledAt || r.accessUntil,
    dateLabel: "Fecha de baja",
    when: "Fecha de baja",
    extra: [
      { key: "neverpaid", label: "Nunca pagaron", test: (r) => !r.paid },
      { key: "paid", label: "Llegaron a pagar", test: (r) => r.paid },
    ],
  },
];

/** Qué pasa después con cada uno, en palabras. */
const nextStep = (r) => {
  switch (r.state) {
    case "paying":
      return `Renueva el ${day(r.accessUntil)}`;
    case "paying_cancelled":
      return `No renovará · acceso hasta el ${day(r.accessUntil)}`;
    case "trial":
      return `Primer cobro el ${day(r.accessUntil)}`;
    case "trial_cancelled":
      return `Canceló · la prueba acaba el ${day(r.accessUntil)}`;
    case "grace":
      return `Reintentando cobro hasta el ${day(r.accessUntil)}`;
    case "lifetime":
      return "Acceso para siempre";
    default:
      if (r.refunded) return `Pagó y se le devolvió el dinero (refund) · acabó el ${day(r.accessUntil)}`;
      return r.paid ? `Pagó y se fue · acceso hasta el ${day(r.accessUntil)}` : `Nunca pagó · acabó el ${day(r.accessUntil)}`;
  }
};

const story = (r) => {
  if (r.plan === "lifetime") return `Compró lifetime el ${dayShort(r.activatedAt)}`;
  const parts = [r.startedAsTrial ? `Prueba gratis el ${dayShort(r.activatedAt)}` : `Se suscribió pagando el ${dayShort(r.activatedAt)}`];
  if (r.startedAsTrial && r.paid && !r.refunded) parts.push(`primer pago el ${dayShort(r.firstPaymentAt)}`);
  if (r.refunded) parts.push("refund");
  if (r.cancelledAt) parts.push(`canceló el ${dayShort(r.cancelledAt)}`);
  return parts.join(" → ");
};

/** Timeline completo de una fila para la ficha de cliente. */
const timelineOf = (r) => {
  const ev = [];
  if (r.plan === "lifetime") {
    ev.push({ t: new Date(r.activatedAt), kind: "lifetime", label: "Compra lifetime", eur: chargeEur(r, "lifetime") });
  } else {
    if (r.startedAsTrial) ev.push({ t: new Date(r.activatedAt), kind: "trial_start", label: "Empieza la prueba gratis" });
    (r.payments ?? []).forEach((p, i) =>
      ev.push({
        t: new Date(p.date),
        kind: p.kind,
        label: p.kind === "first" ? (r.startedAsTrial ? "Primer pago (convierte la prueba)" : "Se suscribe · primer pago") : `Renovación ${i}ª`,
        eur: chargeEur(r, p.kind),
      })
    );
    if (r.cancelledAt) ev.push({ t: new Date(r.cancelledAt), kind: "cancel", label: "Cancela la renovación" });
    if (r.refunded) ev.push({ t: new Date(r.accessUntil || r.cancelledAt || r.activatedAt), kind: "cancel", label: "Refund · la tienda le devolvió el dinero" });
    if (r.state === "ended" && r.accessUntil) ev.push({ t: new Date(r.accessUntil), kind: "end", label: r.refunded ? "Acceso cerrado tras el refund" : r.paid ? "Se le acaba el acceso" : "Acaba la prueba sin pagar" });
  }
  ev.sort((a, b) => a.t - b.t);
  const next = r.state !== "ended" && r.plan !== "lifetime" && r.accessUntil ? { t: new Date(r.accessUntil), label: nextStep(r) } : null;
  return { ev, next };
};

const inRange = (d, p) => d && new Date(d) >= p.from && new Date(d) < p.to;

const buildPeriods = (rows) => {
  const now = new Date();
  const first = rows.length ? new Date(rows[rows.length - 1].activatedAt) : now;
  const months = [];
  for (let d = monthStart(now.getFullYear(), now.getMonth()); d >= monthStart(first.getFullYear(), first.getMonth()); d = monthStart(d.getFullYear(), d.getMonth() - 1)) {
    months.push({ key: monthKey(d), label: `${cap(MESES[d.getMonth()])} ${d.getFullYear()}`, from: d, to: monthStart(d.getFullYear(), d.getMonth() + 1) });
  }
  return { months };
};

/** Barras del gráfico "cuándo" de Clientes: por día si todo cae en ~2 meses. */
const buildWhen = (list, dateOf) => {
  const dates = list.map(dateOf).filter(Boolean).map((d) => new Date(d));
  if (!dates.length) return { mode: "month", buckets: [] };
  const min = new Date(Math.min(...dates));
  const max = new Date(Math.max(...dates, Date.now()));
  const byDay = (max - min) / 864e5 <= 62;
  const buckets = [];
  if (byDay) {
    for (let d = new Date(min.getFullYear(), min.getMonth(), min.getDate()); d <= max; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1))
      buckets.push({ key: `d${d.getTime()}`, label: `${d.getDate()} ${MES_CORTO[d.getMonth()]}`, from: d, to: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1) });
  } else {
    for (let d = monthStart(min.getFullYear(), min.getMonth()); d <= max; d = monthStart(d.getFullYear(), d.getMonth() + 1))
      buckets.push({ key: monthKey(d), label: monthLabel(d), from: d, to: monthStart(d.getFullYear(), d.getMonth() + 1) });
  }
  for (const b of buckets) {
    const inside = list.filter((r) => inRange(dateOf(r), b));
    b.monthly = inside.filter((r) => r.plan === "monthly").length;
    b.yearly = inside.filter((r) => r.plan === "yearly").length;
    b.lifetime = inside.filter((r) => r.plan === "lifetime").length;
    b.total = inside.length;
  }
  return { mode: byDay ? "day" : "month", buckets };
};

/** Lo que va a pasar con cada suscripción viva en los próximos días. */
const UPCOMING = {
  paying: { label: "Renovación", tone: "good", money: true },
  trial: { label: "Primer cobro (acaba la prueba)", tone: "good", money: true },
  paying_cancelled: { label: "Se va (no renueva)", tone: "bad" },
  trial_cancelled: { label: "Acaba la prueba sin pagar", tone: "bad" },
  grace: { label: "Último reintento de cobro", tone: "warn" },
};

const PersonRow = ({ r, date, dateMode = "past", onOpen }) => {
  const st = STATE[r.state] || STATE.ended;
  return (
    <div className={cn(styles.item, styles.clickable)} onClick={() => onOpen && onOpen(r)} role="button" tabIndex={0}>
      <Avatar user={r.user} />
      <div className={styles.who}>
        <div className={styles.name}>{r.user?.name || r.user?.email || "Sin usuario vinculado"}</div>
        <div className={styles.email}>{r.user?.name ? r.user?.email : ""}</div>
      </div>
      <div className={styles.planCol}>
        <PlatformIcon platform={r.platform} />
        <span>{PLAN[r.plan]}</span>
      </div>
      <div className={styles.dateCol}>
        <div>{day(date)}</div>
        <div className={styles.muted}>{date ? (dateMode === "future" ? relDay(date) : relPast(date)) : ""}</div>
      </div>
      <div className={styles.stateCol}>
        <span className={styles.badge} style={{ color: st.color, background: st.bg }}>{st.label}</span>
        <div>{nextStep(r)}</div>
        <div className={styles.muted}>{story(r)}</div>
      </div>
      <div className={styles.ltvCol}>
        <div>{ltvOf(r) ? `≈ ${eur(ltvOf(r))}` : r.refunded ? "refund" : "—"}</div>
        <div className={styles.muted}>{(r.payments ?? []).length ? `${(r.payments ?? []).length} ${(r.payments ?? []).length === 1 ? "cobro" : "cobros"}` : ""}</div>
      </div>
      <div className={styles.gamesCol}>
        <div>{n(r.user?.games30d ?? 0)} part.</div>
        <div className={styles.muted}>{r.user?.gamesTotal ? `${n(r.user.gamesTotal)} tot.` : ""}</div>
      </div>
    </div>
  );
};

/** Un cobro concreto (Transacciones y modales de cobros). */
const ChargeRow = ({ c, onOpen }) => {
  const k = KIND[c.kind];
  return (
    <div className={cn(styles.chItem, styles.clickable)} onClick={() => onOpen && onOpen(c.r)} role="button" tabIndex={0}>
      <div className={styles.dateCol}>
        <div>{dayShort(c.date)}</div>
        <div className={styles.muted}>{relPast(c.date)}</div>
      </div>
      <Avatar user={c.r.user} />
      <div className={styles.who}>
        <div className={styles.name}>{c.r.user?.name || c.r.user?.email || "Sin usuario vinculado"}</div>
        <div className={styles.email}>{c.r.user?.name ? c.r.user?.email : ""}</div>
      </div>
      <div className={styles.planCol}>
        <PlatformIcon platform={c.r.platform} />
        <span>{PLAN[c.r.plan]}</span>
      </div>
      <div>
        <span className={styles.badge} style={{ color: k.color, background: k.bg }}>{k.label}</span>
      </div>
      <div className={styles.eurCol} title={c.r.realPrice ? `${c.r.realPrice.amount.toLocaleString("es-ES")} ${c.r.realPrice.currency}` : "precio de referencia"}>
        ≈ {eur(c.eur)}
      </div>
    </div>
  );
};

/** Una entrada de actividad reciente. */
const FeedRow = ({ e, onOpen }) => {
  const meta = EVENT[e.type];
  return (
    <div className={cn(styles.chItem, styles.clickable)} onClick={() => onOpen && onOpen(e.r)} role="button" tabIndex={0}>
      <div className={styles.dateCol}>
        <div>{dayShort(e.t)}</div>
        <div className={styles.muted}>{relPast(e.t)}</div>
      </div>
      <Avatar user={e.r.user} />
      <div className={styles.who}>
        <div className={styles.name}>{e.r.user?.name || e.r.user?.email || "Sin usuario vinculado"}</div>
        <div className={styles.email}>{e.r.user?.name ? e.r.user?.email : ""}</div>
      </div>
      <div className={styles.planCol}>
        <PlatformIcon platform={e.r.platform} />
        <span>{PLAN[e.r.plan]}</span>
      </div>
      <div>
        <span className={styles.badge} style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
      </div>
      <div className={styles.eurCol}>{e.eur ? `≈ ${eur(e.eur)}` : ""}</div>
    </div>
  );
};

const TABS = [
  { key: "resumen", label: "Resumen" },
  { key: "proximos", label: "Próximos" },
  { key: "transacciones", label: "Transacciones" },
  { key: "clientes", label: "Clientes" },
];
const RANGES = [
  { key: "30d", label: "30 D", days: 30 },
  { key: "90d", label: "90 D", days: 90 },
  { key: "12m", label: "12 M", months: 12 },
  { key: "all", label: "Todo" },
];
const PAGE = 20;

const Subscriptions = () => {
  const darkMode = useDarkMode(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useTabParam(TABS, "resumen");
  const [modal, setModal] = useState(null);
  const [globalQ, setGlobalQ] = useState("");
  // Resumen
  const [rango, setRango] = useState("30d");
  const [metricKey, setMetricKey] = useState("ingresos");
  const [feedKind, setFeedKind] = useState("");
  const [feedAll, setFeedAll] = useState(false);
  // Próximos
  const [upDays, setUpDays] = useState(30);
  // Transacciones
  const [txPeriodKey, setTxPeriodKey] = useState("this");
  const [txKind, setTxKind] = useState("");
  const [txPlatform, setTxPlatform] = useState("");
  const [txQ, setTxQ] = useState("");
  const [txPage, setTxPage] = useState(0);
  // Clientes
  const [segKey, setSegKey] = useState("paying");
  const [plan, setPlan] = useState("");
  const [platform, setPlatform] = useState("");
  const [extra, setExtra] = useState("");
  const [bucket, setBucket] = useState(null);
  const [q, setQ] = useState("");
  const [orden, setOrden] = useState("recientes");
  const [page, setPage] = useState(0);

  useEffect(() => {
    getSubscriptionsStats()
      .then((r) => (r?.status === 200 ? setData(r.data) : setError(true)))
      .catch(() => setError(true));
  }, []);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const periods = useMemo(() => buildPeriods(rows), [rows]);

  const openPeople = (title, subtitle, items, dateOf, dateMode = "past") =>
    setModal({ kind: "people", title, subtitle, items, dateOf, dateMode });
  const openCharges = (title, subtitle, items) => setModal({ kind: "charges", title, subtitle, items });
  const openCustomer = (r) => setModal((m) => ({ kind: "customer", r, back: m && m.kind !== "customer" ? m : m?.back }));

  // ── Cobros y derivados ────────────────────────────────────────────────
  const charges = useMemo(
    () =>
      rows.flatMap((r) =>
        (r.payments ?? []).map((p, i) => ({
          id: `${r._id}:${i}`,
          r,
          date: new Date(p.date),
          kind: p.kind,
          eur: chargeEur(r, p.kind),
        }))
      ),
    [rows]
  );
  const payingAt = (end) => {
    if (Math.abs(end.getTime() - Date.now()) < 60_000) return rows.filter((r) => ["paying", "paying_cancelled"].includes(r.state));
    return rows.filter((r) => r.plan !== "lifetime" && r.firstPaymentAt && new Date(r.firstPaymentAt) < end && (!r.accessUntil || new Date(r.accessUntil) >= end));
  };
  const trialAt = (end) => {
    if (Math.abs(end.getTime() - Date.now()) < 60_000) return rows.filter((r) => ["trial", "trial_cancelled"].includes(r.state));
    return rows.filter((r) => r.startedAsTrial && r.trialEndsAt && new Date(r.activatedAt) <= end && new Date(r.trialEndsAt) > end);
  };
  const mrrOf = (list) =>
    list.reduce((a, r) => {
      if (r.plan === "monthly") return a + (rowPrice(r) ?? PLAN_EUR.monthly);
      if (r.plan === "yearly") return a + (rowPrice(r) ?? PLAN_EUR.yearly) / 12;
      return a;
    }, 0);
  const firstChargeOf = (r) => {
    const p0 = (r.payments ?? [])[0];
    return p0 && (p0.kind === "first" || p0.kind === "lifetime") ? p0.date : null;
  };

  // ── Resumen: rango, cubos, métricas ───────────────────────────────────
  const range = RANGES.find((x) => x.key === rango);
  const buckets = useMemo(() => {
    const now = new Date();
    if (range.days) {
      const out = [];
      for (let i = range.days - 1; i >= 0; i--) {
        const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i + 1);
        out.push({
          key: `${from.getTime()}`,
          label: `${from.getDate()} ${MES_CORTO[from.getMonth()]}`,
          full: `${from.getDate()} ${MES_CORTO[from.getMonth()]}`,
          from,
          to,
          end: new Date(Math.min(to.getTime() - 1, now.getTime())),
        });
      }
      return out;
    }
    const ms = [...periods.months].reverse();
    return (range.months ? ms.slice(-range.months) : ms).map((m) => ({
      key: m.key,
      label: monthLabel(m.from),
      full: m.label.toLowerCase(),
      from: m.from,
      to: m.to,
      end: new Date(Math.min(m.to.getTime() - 1, now.getTime())),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango, periods]);

  const rangeFrom = buckets[0]?.from ?? new Date();
  const rangeTo = new Date();
  const prev = useMemo(() => {
    if (rango === "all") return null;
    const len = rangeTo.getTime() - rangeFrom.getTime();
    return { from: new Date(rangeFrom.getTime() - len), to: rangeFrom };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango, buckets]);

  const stats = useMemo(() => {
    const cur = { from: rangeFrom, to: rangeTo };
    const inCur = (d) => inRange(d, cur);
    const inPrev = (d) => prev && inRange(d, prev);
    const chCur = charges.filter((c) => inCur(c.date));
    const chPrev = prev ? charges.filter((c) => inPrev(c.date)) : null;
    const payingNow = payingAt(rangeTo);
    const payingStart = payingAt(rangeFrom);
    const trialsEnded = rows.filter((r) => r.startedAsTrial && r.trialEndsAt && inCur(r.trialEndsAt) && new Date(r.trialEndsAt) <= rangeTo);
    return {
      altas: rows.filter((r) => inCur(r.activatedAt)),
      altasPrev: prev ? rows.filter((r) => inPrev(r.activatedAt)).length : null,
      trialsEnded,
      ingresos: chCur.reduce((a, c) => a + c.eur, 0),
      ingresosPrev: chPrev ? chPrev.reduce((a, c) => a + c.eur, 0) : null,
      cobros: chCur.length,
      mrrNow: mrrOf(payingNow),
      mrrStart: mrrOf(payingStart),
      activasNow: payingNow.length,
      activasStart: payingStart.length,
      enPruebaNow: trialAt(rangeTo).length,
      enPruebaStart: trialAt(rangeFrom).length,
      nuevos: rows.filter((r) => firstChargeOf(r) && inCur(firstChargeOf(r))),
      nuevosPrev: prev ? rows.filter((r) => firstChargeOf(r) && inPrev(firstChargeOf(r))).length : null,
      conversion: pct(trialsEnded.filter((r) => r.paid).length, trialsEnded.length),
      conversionPrev: prev
        ? (() => {
            const te = rows.filter((r) => r.startedAsTrial && r.trialEndsAt && inPrev(r.trialEndsAt));
            return pct(te.filter((r) => r.paid).length, te.length);
          })()
        : null,
      cancelaciones: rows.filter((r) => r.cancelledAt && inCur(r.cancelledAt)),
      cancelacionesPrev: prev ? rows.filter((r) => r.cancelledAt && inPrev(r.cancelledAt)).length : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, charges, rango, buckets]);

  const series = useMemo(() => {
    return buckets.map((b) => {
      const cs = charges.filter((c) => c.date >= b.from && c.date < b.to);
      const g = (kind) => cs.filter((c) => c.kind === kind);
      const paying = payingAt(b.end);
      return {
        ...b,
        cs,
        cobros: cs.length,
        eur: Math.round(cs.reduce((a, c) => a + c.eur, 0)),
        eurRenewals: Math.round(g("renewal").reduce((a, c) => a + c.eur, 0)),
        eurFirsts: Math.round(g("first").reduce((a, c) => a + c.eur, 0)),
        eurLifetimes: Math.round(g("lifetime").reduce((a, c) => a + c.eur, 0)),
        renewals: g("renewal").length,
        firsts: g("first").length,
        lifetimes: g("lifetime").length,
        mrr: Math.round(mrrOf(paying)),
        mrrMensual: Math.round(mrrOf(paying.filter((r) => r.plan === "monthly"))),
        mrrAnual: Math.round(mrrOf(paying.filter((r) => r.plan === "yearly"))),
        activas: paying.length,
        activasMensual: paying.filter((r) => r.plan === "monthly").length,
        activasAnual: paying.filter((r) => r.plan === "yearly").length,
        enPrueba: trialAt(b.end).length,
        altas: rows.filter((r) => inRange(r.activatedAt, b)).length,
        nuevos: rows.filter((r) => firstChargeOf(r) && inRange(firstChargeOf(r), b)).length,
        convertidas: rows.filter((r) => r.startedAsTrial && r.paid && r.trialEndsAt && inRange(r.trialEndsAt, b) && new Date(r.trialEndsAt) <= new Date()).length,
        noConvertidas: rows.filter((r) => r.startedAsTrial && !r.paid && r.trialEndsAt && inRange(r.trialEndsAt, b) && new Date(r.trialEndsAt) <= new Date()).length,
        cancelaciones: rows.filter((r) => r.cancelledAt && inRange(r.cancelledAt, b)).length,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buckets, charges, rows]);

  const METRICS = [
    {
      key: "ingresos",
      label: "Ingresos",
      value: `≈ ${eur(stats.ingresos)}`,
      sub: `${n(stats.cobros)} ${stats.cobros === 1 ? "cobro" : "cobros"}`,
      delta: prev ? { now: stats.ingresos, before: stats.ingresosPrev, fmt: "pct" } : null,
      color: "#2A85FF",
      dataKey: "eur",
      spark: (s) => s.eur,
      stacks: [
        ["eurRenewals", KIND.renewal.color, "Renovaciones"],
        ["eurFirsts", "#83BF6E", "Primeros pagos"],
        ["eurLifetimes", "#FFB020", "Lifetime"],
      ],
      fmtTick: (v) => `${v} €`,
      onBucket: (b) => b.cobros && openCharges(`Cobros · ${b.full}`, `≈ ${eur(b.eur)}`, b.cs),
    },
    {
      key: "mrr",
      label: "MRR",
      value: `≈ ${eur(stats.mrrNow)}`,
      sub: "al mes si nada cambia · anual entre 12",
      delta: prev ? { now: stats.mrrNow, before: stats.mrrStart, fmt: "eur" } : null,
      color: "#8E59FF",
      dataKey: "mrr",
      spark: (s) => s.mrr,
      areas: [
        ["mrrMensual", PLAN_COLOR.monthly, "Mensual"],
        ["mrrAnual", PLAN_COLOR.yearly, "Anual (entre 12)"],
      ],
      fmtTick: (v) => `${v} €`,
      onBucket: (b) => openPeople(`De pago al cierre · ${b.full}`, `MRR ≈ ${eur(b.mrr)}`, payingAt(b.end), (r) => r.firstPaymentAt),
    },
    {
      key: "activas",
      label: "Suscripciones de pago",
      value: n(stats.activasNow),
      sub: `${n(rows.filter((r) => ["paying", "paying_cancelled"].includes(r.state) && r.plan === "monthly").length)} mensual · ${n(
        rows.filter((r) => ["paying", "paying_cancelled"].includes(r.state) && r.plan === "yearly").length
      )} anual · ${n(rows.filter((r) => r.state === "paying_cancelled").length)} no renovarán`,
      delta: prev ? { now: stats.activasNow, before: stats.activasStart, fmt: "abs" } : null,
      color: "#B1E5FC",
      dataKey: "activas",
      spark: (s) => s.activas,
      areas: [
        ["activasMensual", PLAN_COLOR.monthly, "Mensual"],
        ["activasAnual", PLAN_COLOR.yearly, "Anual"],
      ],
      onBucket: (b) => openPeople(`De pago al cierre · ${b.full}`, null, payingAt(b.end), (r) => r.firstPaymentAt),
    },
    {
      key: "enprueba",
      label: "En prueba ahora",
      value: n(stats.enPruebaNow),
      sub: `${n(rows.filter((r) => r.state === "trial").length)} sin cancelar · ${n(rows.filter((r) => r.state === "trial_cancelled").length)} ya cancelaron`,
      delta: prev ? { now: stats.enPruebaNow, before: stats.enPruebaStart, fmt: "abs" } : null,
      color: "#8E59FF",
      dataKey: "enPrueba",
      spark: (s) => s.enPrueba,
      area: true,
      onBucket: (b) => openPeople(`En prueba · ${b.full}`, null, trialAt(b.end), (r) => r.activatedAt),
    },
    {
      key: "altas",
      label: "Altas",
      value: n(stats.altas.length),
      sub: `${n(stats.altas.filter((r) => r.startedAsTrial).length)} con prueba · ${n(stats.altas.filter((r) => !r.startedAsTrial).length)} ${
        stats.altas.filter((r) => !r.startedAsTrial).length === 1 ? "directa" : "directas"
      }`,
      delta: prev ? { now: stats.altas.length, before: stats.altasPrev, fmt: "pct" } : null,
      color: "#FFB020",
      dataKey: "altas",
      spark: (s) => s.altas,
      onBucket: (b) => openPeople(`Altas · ${b.full}`, null, rows.filter((r) => inRange(r.activatedAt, b)), (r) => r.activatedAt),
    },
    {
      key: "nuevos",
      label: "Nuevos clientes",
      value: n(stats.nuevos.length),
      sub: "su primer pago o lifetime",
      delta: prev ? { now: stats.nuevos.length, before: stats.nuevosPrev, fmt: "pct" } : null,
      color: "#83BF6E",
      dataKey: "nuevos",
      spark: (s) => s.nuevos,
      onBucket: (b) =>
        openPeople(`Nuevos clientes · ${b.full}`, null, rows.filter((r) => firstChargeOf(r) && inRange(firstChargeOf(r), b)), (r) => firstChargeOf(r)),
    },
    {
      key: "conversion",
      label: "Conversión de pruebas",
      value: stats.conversion == null ? "—" : `${stats.conversion}%`,
      sub: `${n(stats.trialsEnded.filter((r) => r.paid).length)} de ${n(stats.trialsEnded.length)} terminadas pagaron`,
      delta:
        prev && stats.conversion != null && stats.conversionPrev != null
          ? { now: stats.conversion, before: stats.conversionPrev, fmt: "pp" }
          : null,
      color: "#83BF6E",
      dataKey: "convertidas",
      spark: (s) => s.convertidas + s.noConvertidas,
      stacks: [
        ["convertidas", "#83BF6E", "Pagaron"],
        ["noConvertidas", "#FF6A55", "No pagaron"],
      ],
      onBucket: (b) =>
        openPeople(
          `Pruebas terminadas · ${b.full}`,
          "El estado dice si acabó pagando",
          rows.filter((r) => r.startedAsTrial && r.trialEndsAt && inRange(r.trialEndsAt, b) && new Date(r.trialEndsAt) <= new Date()),
          (r) => r.trialEndsAt
        ),
    },
    {
      key: "cancelaciones",
      label: "Cancelaciones",
      value: n(stats.cancelaciones.length),
      sub: `${n(stats.cancelaciones.filter((r) => r.paid).length)} de pago${
        stats.activasStart ? ` · churn ${pct(stats.cancelaciones.filter((r) => r.paid).length, stats.activasStart)}%` : ""
      }`,
      delta: prev ? { now: stats.cancelaciones.length, before: stats.cancelacionesPrev, fmt: "pct", invert: true } : null,
      color: "#FF6A55",
      dataKey: "cancelaciones",
      spark: (s) => s.cancelaciones,
      onBucket: (b) =>
        openPeople(`Cancelaciones · ${b.full}`, null, rows.filter((r) => r.cancelledAt && inRange(r.cancelledAt, b)), (r) => r.cancelledAt),
    },
  ];
  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0];

  // ── Actividad reciente ────────────────────────────────────────────────
  const feed = useMemo(() => {
    const now = new Date();
    const ev = [];
    rows.forEach((r) => {
      (r.payments ?? []).forEach((p, i) => ev.push({ id: `${r._id}:p${i}`, t: new Date(p.date), type: p.kind, r, eur: chargeEur(r, p.kind) }));
      if (r.startedAsTrial) ev.push({ id: `${r._id}:t`, t: new Date(r.activatedAt), type: "trial_start", r });
      if (r.cancelledAt) ev.push({ id: `${r._id}:c`, t: new Date(r.cancelledAt), type: "cancel", r });
    });
    return ev.filter((e) => e.t <= now).sort((a, b) => b.t - a.t);
  }, [rows]);
  const feedFiltered = useMemo(
    () =>
      feed.filter((e) =>
        !feedKind ? true : feedKind === "money" ? ["first", "renewal", "lifetime"].includes(e.type) : e.type === feedKind
      ),
    [feed, feedKind]
  );

  // ── Top clientes ──────────────────────────────────────────────────────
  const topClientes = useMemo(() => [...rows].filter((r) => ltvOf(r) > 0).sort((a, b) => ltvOf(b) - ltvOf(a)), [rows]);

  // ── Próximos ──────────────────────────────────────────────────────────
  const upcoming = useMemo(() => {
    const now = Date.now();
    const until = now + upDays * 864e5;
    const ev = rows
      .filter((r) => UPCOMING[r.state] && r.accessUntil)
      .filter((r) => {
        const t = new Date(r.accessUntil).getTime();
        return t >= now - 864e5 && t <= until;
      })
      .sort((a, b) => new Date(a.accessUntil) - new Date(b.accessUntil));
    const chargesEv = ev.filter((r) => UPCOMING[r.state].money);
    return {
      ev,
      chargesEv,
      leaving: ev.filter((r) => r.state === "paying_cancelled" || r.state === "trial_cancelled"),
      graceEv: ev.filter((r) => r.state === "grace"),
      renewals: ev.filter((r) => r.state === "paying").length,
      firstCharges: ev.filter((r) => r.state === "trial").length,
      money: chargesEv.reduce((a, r) => a + chargeEur(r, "first"), 0),
    };
  }, [rows, upDays]);

  // Los mismos números que la agenda a 7 días (para el aviso del Resumen y no contradecirse).
  const next7 = useMemo(() => {
    const now = Date.now();
    const ev = rows.filter((r) => UPCOMING[r.state] && r.accessUntil).filter((r) => {
      const t = new Date(r.accessUntil).getTime();
      return t >= now - 864e5 && t <= now + 7 * 864e5;
    });
    const moneyEv = ev.filter((r) => UPCOMING[r.state].money);
    return {
      money: moneyEv.length,
      moneyEur: moneyEv.reduce((a, r) => a + chargeEur(r, "first"), 0),
      leaving: ev.filter((r) => r.state === "paying_cancelled" || r.state === "trial_cancelled").length,
    };
  }, [rows]);

  // ── Transacciones ─────────────────────────────────────────────────────
  const TX_PERIODS = useMemo(() => {
    const now = new Date();
    return [
      { key: "this", label: `Este mes (${MES_CORTO[now.getMonth()]})`, from: monthStart(now.getFullYear(), now.getMonth()), to: now },
      { key: "30d", label: "Últimos 30 días", from: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29), to: now },
      { key: "90d", label: "Últimos 90 días", from: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89), to: now },
      { key: "12m", label: "Últimos 12 meses", from: monthStart(now.getFullYear(), now.getMonth() - 11), to: now },
      { key: "all", label: "Todo el histórico", from: new Date(2000, 0, 1), to: now },
      ...periods.months.map((m) => ({ key: m.key, label: m.label, from: m.from, to: m.to })),
    ];
  }, [periods]);
  const txRange = TX_PERIODS.find((x) => x.key === txPeriodKey) ?? TX_PERIODS[0];
  const txCharges = useMemo(
    () => charges.filter((c) => c.date >= txRange.from && c.date < txRange.to).sort((a, b) => b.date - a.date),
    [charges, txRange]
  );
  const txFiltered = useMemo(() => {
    const qq = txQ.trim().toLowerCase();
    return txCharges
      .filter((c) => !txKind || c.kind === txKind)
      .filter((c) => !txPlatform || c.r.platform === txPlatform)
      .filter((c) => !qq || `${c.r.user?.email ?? ""} ${c.r.user?.name ?? ""}`.toLowerCase().includes(qq));
  }, [txCharges, txKind, txPlatform, txQ]);
  const txBuckets = useMemo(() => {
    const now = new Date();
    const start = txRange.key === "all" && rows.length ? new Date(rows[rows.length - 1].activatedAt) : txRange.from;
    const end = new Date(Math.min(txRange.to.getTime(), now.getTime()));
    const byDay = (end.getTime() - start.getTime()) / 864e5 <= 92;
    const out = [];
    if (byDay)
      for (let d = new Date(start.getFullYear(), start.getMonth(), start.getDate()); d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1))
        out.push({ from: d, to: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1), label: `${d.getDate()} ${MES_CORTO[d.getMonth()]}`, full: `${d.getDate()} ${MES_CORTO[d.getMonth()]}` });
    else
      for (let d = monthStart(start.getFullYear(), start.getMonth()); d < end; d = monthStart(d.getFullYear(), d.getMonth() + 1))
        out.push({ from: d, to: monthStart(d.getFullYear(), d.getMonth() + 1), label: monthLabel(d), full: `${MESES[d.getMonth()]} ${d.getFullYear()}` });
    return out.map((x) => {
      const cs = txCharges.filter((c) => c.date >= x.from && c.date < x.to);
      const g = (kind) => cs.filter((c) => c.kind === kind);
      return {
        ...x,
        key: `${x.from.getTime()}`,
        count: cs.length,
        cs,
        eur: cs.reduce((a, c) => a + c.eur, 0),
        eurRenewals: g("renewal").reduce((a, c) => a + c.eur, 0),
        eurFirsts: g("first").reduce((a, c) => a + c.eur, 0),
        eurLifetimes: g("lifetime").reduce((a, c) => a + c.eur, 0),
      };
    });
  }, [txCharges, txRange, rows]);

  // ── Clientes ──────────────────────────────────────────────────────────
  const seg = SEGMENTS.find((s) => s.key === segKey);
  const segRows = useMemo(() => rows.filter((r) => seg.states.includes(r.state)), [rows, seg]);
  const chooseSegment = (key) => {
    setSegKey(key);
    setPlan("");
    setPlatform("");
    setExtra("");
    setBucket(null);
    setQ("");
    setPage(0);
  };
  const filtered = useMemo(() => {
    const ex = seg.extra.find((e) => e.key === extra);
    return segRows.filter((r) => (!plan || r.plan === plan) && (!platform || r.platform === platform) && (!ex || ex.test(r)));
  }, [segRows, seg, plan, platform, extra]);
  const when = useMemo(() => buildWhen(filtered, seg.date), [filtered, seg]);
  const list = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return filtered
      .filter((r) => !bucket || inRange(seg.date(r), bucket))
      .filter((r) => !qq || `${r.user?.email ?? ""} ${r.user?.name ?? ""}`.toLowerCase().includes(qq))
      .sort((a, b) =>
        orden === "ltv"
          ? ltvOf(b) - ltvOf(a)
          : orden === "juega"
          ? (b.user?.games30d ?? 0) - (a.user?.games30d ?? 0)
          : orden === "antiguos"
          ? new Date(seg.date(a) || 0) - new Date(seg.date(b) || 0)
          : new Date(seg.date(b) || 0) - new Date(seg.date(a) || 0)
      );
  }, [filtered, bucket, q, seg, orden]);

  const exportCsv = () => {
    const head = ["email", "nombre", "plan", "tienda", "estado", seg.dateLabel.toLowerCase().replace(/ /g, "_"), "pagado_eur", "cobros", "partidas_30d", "partidas_total"];
    const body = list.map((r) => [
      r.user?.email ?? "",
      r.user?.name ?? "",
      PLAN[r.plan],
      r.platform === "APPLE" ? "iOS" : "Android",
      STATE[r.state]?.label ?? r.state,
      seg.date(r) ? new Date(seg.date(r)).toISOString().slice(0, 10) : "",
      ltvOf(r).toFixed(2).replace(".", ","),
      (r.payments ?? []).length,
      r.user?.games30d ?? 0,
      r.user?.gamesTotal ?? 0,
    ]);
    const csv = [head, ...body].map((row) => row.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `clientes-${seg.key}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const globalResults = useMemo(() => {
    const qq = globalQ.trim().toLowerCase();
    if (qq.length < 2) return null;
    return rows.filter((r) => `${r.user?.email ?? ""} ${r.user?.name ?? ""}`.toLowerCase().includes(qq));
  }, [rows, globalQ]);

  if (error || !data)
    return (
      <Card className={styles.card} title="Suscripciones" classTitle="title-green">
        <div className={styles.state}>{error ? "No se pudieron cargar las suscripciones" : "Cargando…"}</div>
      </Card>
    );

  const grid = darkMode.value ? "#33383F" : "#EFEFEF";
  const count = (states) => rows.filter((r) => states.includes(r.state)).length;
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const pageRows = list.slice(page * PAGE, page * PAGE + PAGE);
  const txPages = Math.max(1, Math.ceil(txFiltered.length / PAGE));
  const txPageRows = txFiltered.slice(txPage * PAGE, txPage * PAGE + PAGE);
  const planKeys = ["monthly", "yearly", "lifetime"].filter((p) => segRows.some((r) => r.plan === p));
  const setF = (setter) => (v) => {
    setter(v);
    setBucket(null);
    setPage(0);
  };
  const pickBucket = (b) => {
    if (!b?.total) return;
    setBucket(bucket?.key === b.key ? null : b);
    setPage(0);
  };
  const headSort = (key, def = "recientes") => {
    if (key === "fecha") setOrden(orden === "recientes" ? "antiguos" : "recientes");
    else setOrden(orden === key ? def : key);
    setPage(0);
  };

  const chartTooltip = ({ active, payload }) => {
    const b = payload?.[0]?.payload;
    if (!active || !b) return null;
    const parts = metric.stacks || metric.areas;
    return (
      <div className={styles.tooltip}>
        <strong>{b.full}</strong>
        {metric.key === "ingresos" && <div>≈ {eur(b.eur)} · {b.cobros} {b.cobros === 1 ? "cobro" : "cobros"}</div>}
        {parts ? (
          parts.map(([k, , label]) => (b[k] ? <div key={k}>{label}: {metric.fmtTick ? `≈ ${eur(b[k])}` : n(b[k])}</div> : null))
        ) : (
          <div>{metric.label}: {metric.fmtTick ? `≈ ${eur(b[metric.dataKey])}` : n(b[metric.dataKey])}</div>
        )}
        {metric.key === "activas" && <div>Total: {n(b.activas)}</div>}
        {metric.key === "mrr" && <div>Total: ≈ {eur(b.mrr)}</div>}
        {metric.key === "conversion" && b.convertidas + b.noConvertidas > 0 && <div>Conversión: {pct(b.convertidas, b.convertidas + b.noConvertidas)}%</div>}
      </div>
    );
  };
  const axisProps = { tickLine: false, axisLine: false, tick: { fontSize: 11, fill: "#9A9FA5" } };
  const chartAreas = metric.areas || (metric.area ? [[metric.dataKey, metric.color, metric.label]] : null);

  // Timeline de la ficha de cliente
  const cust = modal?.kind === "customer" ? modal.r : null;
  const custTl = cust ? timelineOf(cust) : null;

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <TabsHeader
          tabs={TABS.map((t) => (t.key === "proximos" ? { ...t, count: upcoming.ev.length } : t))}
          active={tab}
          onChange={setTab}
        />
        <input className={styles.globalSearch} placeholder="Buscar cliente…" value={globalQ} onChange={(e) => setGlobalQ(e.target.value)} />
        <span className={cn(styles.muted, styles.updatedAt)}>
          Datos · {new Date(data.generatedAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>

      {globalResults && (
        <Card
          className={styles.card}
          title={`${n(globalResults.length)} ${globalResults.length === 1 ? "resultado" : "resultados"}`}
          classTitle="title-blue"
          head={
            <button type="button" className={styles.linkBtn} onClick={() => setGlobalQ("")}>
              Cerrar ✕
            </button>
          }
        >
          <div className={styles.list}>
            {globalResults.length === 0 && <div className={styles.empty}>Sin resultados</div>}
            {globalResults.slice(0, 50).map((r) => (
              <PersonRow key={r._id} r={r} date={r.activatedAt} onOpen={openCustomer} />
            ))}
            {globalResults.length > 50 && <div className={styles.empty}>Mostrando 50 de {n(globalResults.length)}</div>}
          </div>
        </Card>
      )}

      {/* ════ RESUMEN ════ */}
      {tab === "resumen" && (
        <>
          <div className={styles.rangeRow}>
            <button type="button" className={styles.miniStrip} onClick={() => { setUpDays(7); setTab("proximos"); }}>
              Próximos 7 días: <strong>{n(next7.money)} cobros ≈ {eur(next7.moneyEur)}</strong> · {n(next7.leaving)} se van · Ver agenda ›
            </button>
            <div className={styles.ranges}>
              {RANGES.map((r) => (
                <button key={r.key} type="button" className={cn(styles.rangeBtn, { [styles.rangeActive]: rango === r.key })} onClick={() => setRango(r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <KpiGrid>
            {METRICS.map((m) => (
              <KpiTile
                key={m.key}
                label={m.label}
                value={m.value}
                sub={m.sub}
                delta={m.delta}
                spark={series.map(m.spark)}
                color={m.color}
                active={m.key === metricKey}
                onClick={() => setMetricKey(m.key)}
                formatMoney={eur}
              />
            ))}
          </KpiGrid>

          <Card
            className={styles.card}
            title={metric.label}
            classTitle="title-blue"
            head={<span className={styles.muted}>{range.key === "all" ? "todo el histórico" : `últimos ${range.label.toLowerCase().replace(" ", "")}`} · clic en el gráfico para ver el detalle</span>}
          >
            <ResponsiveContainer width="100%" height={280}>
              {chartAreas ? (
                <AreaChart
                  data={series}
                  margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    const b = e?.activePayload?.[0]?.payload;
                    if (b) metric.onBucket(b);
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
                  <XAxis dataKey="label" {...axisProps} minTickGap={24} />
                  <YAxis {...axisProps} width={52} tickFormatter={metric.fmtTick} allowDecimals={false} />
                  <Tooltip content={chartTooltip} />
                  {chartAreas.map(([k, color]) => (
                    <Area key={k} stackId="s" type="monotone" dataKey={k} stroke={color} strokeWidth={2} fill={color} fillOpacity={0.25} />
                  ))}
                </AreaChart>
              ) : (
                <BarChart data={series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
                  <XAxis dataKey="label" {...axisProps} minTickGap={24} />
                  <YAxis {...axisProps} width={52} tickFormatter={metric.fmtTick} allowDecimals={false} />
                  <Tooltip cursor={{ fill: darkMode.value ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }} content={chartTooltip} />
                  {(metric.stacks || [[metric.dataKey, metric.color]]).map(([k, color], i, arr) => (
                    <Bar
                      key={k}
                      dataKey={k}
                      stackId={metric.stacks ? "e" : undefined}
                      fill={color}
                      maxBarSize={22}
                      radius={i === arr.length - 1 ? [4, 4, 0, 0] : 0}
                      onClick={(d) => metric.onBucket(d?.payload ?? d)}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
            {(metric.stacks || metric.areas) && (
              <div className={styles.legend}>
                {(metric.stacks || metric.areas).map(([k, color, label]) => (
                  <span key={k}><i style={{ background: color }} /> {label}</span>
                ))}
              </div>
            )}
          </Card>

          <div className={styles.split}>
            <Card
              className={styles.card}
              title="Actividad reciente"
              classTitle="title-green"
              head={
                <div className={styles.chipRow}>
                  <Chip on={!feedKind} onClick={() => setFeedKind("")}>Todo</Chip>
                  <Chip on={feedKind === "money"} onClick={() => setFeedKind(feedKind === "money" ? "" : "money")}>Cobros</Chip>
                  <Chip on={feedKind === "trial_start"} onClick={() => setFeedKind(feedKind === "trial_start" ? "" : "trial_start")}>Pruebas</Chip>
                  <Chip on={feedKind === "cancel"} onClick={() => setFeedKind(feedKind === "cancel" ? "" : "cancel")}>Cancelaciones</Chip>
                </div>
              }
            >
              <div className={styles.list}>
                {feedFiltered.slice(0, feedAll ? 30 : 10).map((e) => (
                  <FeedRow key={e.id} e={e} onOpen={openCustomer} />
                ))}
                {feedFiltered.length === 0 && <div className={styles.empty}>Sin actividad</div>}
              </div>
              {feedFiltered.length > 10 && (
                <button type="button" className={styles.linkBtn} onClick={() => setFeedAll(!feedAll)}>
                  {feedAll ? "Ver menos" : "Ver más"}
                </button>
              )}
            </Card>

            <div className={styles.sideCol}>
              <Card className={styles.card} title="Estados ahora" classTitle="title-purple" head={<span className={styles.muted}>clic para ver quiénes</span>}>
                <div className={styles.estadoList}>
                  {SEGMENTS.map((s) => {
                    const l = rows.filter((r) => s.states.includes(r.state));
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={styles.estadoRow}
                        onClick={() => openPeople(`${s.title} · ahora`, null, [...l].sort((a, b) => new Date(s.date(b) || 0) - new Date(s.date(a) || 0)), s.date)}
                      >
                        <span className={styles.dot} style={{ background: s.color }} />
                        <span className={styles.estadoLabel}>{s.title}</span>
                        <span className={styles.estadoSplit}>
                          <FaApple size={11} /> {n(l.filter((r) => r.platform === "APPLE").length)} <FaAndroid size={11} color="#3DDC84" /> {n(l.filter((r) => r.platform === "GOOGLE").length)}
                        </span>
                        <strong>{n(l.length)}</strong>
                      </button>
                    );
                  })}
                </div>
              </Card>

              <Card
                className={styles.card}
                title="Top clientes"
                classTitle="title-yellow"
                head={
                  <button type="button" className={styles.linkBtn} onClick={() => openPeople("Top clientes por pagado", "Ordenados por total pagado", topClientes.slice(0, 50), (r) => r.firstPaymentAt)}>
                    Ver 50 ›
                  </button>
                }
              >
                <div className={styles.estadoList}>
                  {topClientes.slice(0, 6).map((r, i) => (
                    <button key={r._id} type="button" className={styles.estadoRow} onClick={() => openCustomer(r)}>
                      <span className={cn(styles.muted, styles.rankNum)}>{i + 1}</span>
                      <Avatar user={r.user} />
                      <span className={styles.estadoLabel}>{r.user?.name || r.user?.email || "—"}</span>
                      <span className={styles.muted}>{n(r.user?.games30d ?? 0)} part.</span>
                      <strong>≈ {eur(ltvOf(r))}</strong>
                    </button>
                  ))}
                </div>
              </Card>
            </div>
          </div>

          <div className={styles.footNote}>
            {n(data.users.total)} usuarios · {n(data.users.lifetimeGrant)} con premium del lanzamiento (nov-2024) · {n(count(["paying", "paying_cancelled", "lifetime"]))} pagando o lifetime · {n(Math.max(0, data.users.total - data.users.lifetimeGrant - count(["paying", "paying_cancelled", "lifetime"])))} usan la app gratis{data.internalExcluded ? ` · ${n(data.internalExcluded)} suscripciones internas excluidas` : ""}
            <br />
            Cobros reconstruidos de los periodos cubiertos · € con el precio real de Apple convertido (tipos aprox.) y de referencia en Google (5,99 € mes · 49,90 € año · 149,90 € lifetime) · un cobro anual o lifetime cuenta entero en su mes (el MRR lo reparte entre 12)
          </div>
        </>
      )}

      {/* ════ PRÓXIMOS ════ */}
      {tab === "proximos" && (
        <Card
          className={styles.card}
          title="Agenda"
          classTitle="title-blue"
          head={
            <div className={styles.ranges}>
              {[7, 14, 30, 60].map((d) => (
                <button key={d} type="button" className={cn(styles.rangeBtn, { [styles.rangeActive]: upDays === d })} onClick={() => setUpDays(d)}>
                  {d} D
                </button>
              ))}
            </div>
          }
        >
          <div className={styles.upStats}>
            <button type="button" className={styles.upStat} onClick={() => openPeople(`Cobros previstos · ${upDays} días`, `≈ ${eur(upcoming.money)}`, upcoming.chargesEv, (r) => r.accessUntil, "future")}>
              <span className={styles.muted}>Cobros previstos</span>
              <strong className={styles.good}>{n(upcoming.chargesEv.length)} <small>≈ {eur(upcoming.money)}</small></strong>
              <span className={styles.muted}>{n(upcoming.renewals)} renovaciones · {n(upcoming.firstCharges)} primeros cobros</span>
            </button>
            <button type="button" className={styles.upStat} onClick={() => openPeople(`Se van · ${upDays} días`, null, upcoming.leaving, (r) => r.accessUntil, "future")}>
              <span className={styles.muted}>Se van</span>
              <strong className={styles.bad}>{n(upcoming.leaving.length)}</strong>
              <span className={styles.muted}>cancelaron; se les acaba el acceso</span>
            </button>
            <button type="button" className={styles.upStat} onClick={() => openPeople("Cobro fallido · último reintento", null, upcoming.graceEv, (r) => r.accessUntil, "future")}>
              <span className={styles.muted}>Cobro fallido</span>
              <strong>{n(upcoming.graceEv.length)}</strong>
              <span className={styles.muted}>la tienda deja de reintentar</span>
            </button>
          </div>

          <div className={styles.timeline}>
            {upcoming.ev.map((r, i) => {
              const u = UPCOMING[r.state];
              const prevEv = upcoming.ev[i - 1];
              const newDay = !prevEv || calDay(prevEv.accessUntil) !== calDay(r.accessUntil);
              return (
                <React.Fragment key={r._id}>
                  {newDay && (
                    <div className={styles.tlDayHead}>
                      {day(r.accessUntil)} <span className={styles.muted}>· {relDay(r.accessUntil)}</span>
                    </div>
                  )}
                  <div className={cn(styles.tlItem, styles.clickable)} onClick={() => openCustomer(r)} role="button" tabIndex={0}>
                    <span className={cn(styles.tlDot, styles[u.tone])} />
                    <Avatar user={r.user} />
                    <div className={styles.who}>
                      <div className={styles.name}>{r.user?.name || r.user?.email || "Sin usuario"}</div>
                      <div className={styles.email}>
                        <PlatformIcon platform={r.platform} size={11} /> {PLAN[r.plan]}
                      </div>
                    </div>
                    <div className={cn(styles.tlWhat, styles[u.tone])}>
                      {u.label}
                      {u.money && <span> · ≈ {eur(chargeEur(r, "first"))}</span>}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            {upcoming.ev.length === 0 && <div className={styles.empty}>Nada previsto en {upDays} días</div>}
          </div>
        </Card>
      )}

      {/* ════ TRANSACCIONES ════ */}
      {tab === "transacciones" && (
        <Card
          className={styles.card}
          title="Transacciones"
          classTitle="title-yellow"
          head={
            <select
              className={styles.periodSelect}
              value={txRange.key}
              onChange={(e) => {
                setTxPeriodKey(e.target.value);
                setTxKind("");
                setTxPlatform("");
                setTxQ("");
                setTxPage(0);
              }}
            >
              {TX_PERIODS.map((x) => (
                <option key={x.key} value={x.key}>{x.label}</option>
              ))}
            </select>
          }
        >
          <div className={styles.txStats}>
            <div className={styles.metric}>
              <div className={styles.metricLabel}>Ingresos</div>
              <div className={cn(styles.metricValue, styles.good)}>≈ {eur(txCharges.reduce((a, c) => a + c.eur, 0))}</div>
              <div className={styles.metricSub}>{n(txCharges.length)} {txCharges.length === 1 ? "cobro" : "cobros"}</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLabel}>Renovaciones</div>
              <div className={styles.metricValue}>{n(txCharges.filter((c) => c.kind === "renewal").length)}</div>
              <div className={styles.metricSub}>≈ {eur(txCharges.filter((c) => c.kind === "renewal").reduce((a, c) => a + c.eur, 0))}</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLabel}>Primeros pagos</div>
              <div className={styles.metricValue}>{n(txCharges.filter((c) => c.kind === "first").length)}</div>
              <div className={styles.metricSub}>≈ {eur(txCharges.filter((c) => c.kind === "first").reduce((a, c) => a + c.eur, 0))}</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLabel}>Lifetime</div>
              <div className={styles.metricValue}>{n(txCharges.filter((c) => c.kind === "lifetime").length)}</div>
              <div className={styles.metricSub}>≈ {eur(txCharges.filter((c) => c.kind === "lifetime").reduce((a, c) => a + c.eur, 0))}</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLabel}>Ticket medio</div>
              <div className={styles.metricValue}>{txCharges.length ? `≈ ${eur(txCharges.reduce((a, c) => a + c.eur, 0) / txCharges.length)}` : "—"}</div>
              <div className={styles.metricSub}>por cobro</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLabel}>Clientes que pagaron</div>
              <div className={styles.metricValue}>{n(new Set(txCharges.map((c) => c.r._id)).size)}</div>
              <div className={styles.metricSub}>distintos en el periodo</div>
            </div>
          </div>

          {txBuckets.length > 1 && (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={txBuckets} margin={{ top: 4, right: 8, left: -8, bottom: 0 }} style={{ cursor: "pointer" }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
                <XAxis dataKey="label" {...axisProps} minTickGap={16} />
                <YAxis {...axisProps} width={52} tickFormatter={(v) => `${Math.round(v)} €`} />
                <Tooltip
                  cursor={{ fill: darkMode.value ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}
                  content={({ active, payload }) => {
                    const d = payload?.[0]?.payload;
                    return active && d ? (
                      <div className={styles.tooltip}>
                        <strong>{d.full}</strong>
                        <div>≈ {eur(d.eur)} · {d.count} {d.count === 1 ? "cobro" : "cobros"}</div>
                      </div>
                    ) : null;
                  }}
                />
                {[
                  ["eurRenewals", KIND.renewal.color, 0],
                  ["eurFirsts", "#83BF6E", 0],
                  ["eurLifetimes", "#FFB020", 1],
                ].map(([k, color, top]) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="d"
                    fill={color}
                    maxBarSize={16}
                    radius={top ? [4, 4, 0, 0] : 0}
                    onClick={(d) => {
                      const x = d?.payload ?? d;
                      if (x?.count) openCharges(`Cobros · ${x.full}`, `≈ ${eur(x.eur)}`, x.cs);
                    }}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}

          <div className={styles.listHead}>
            <input
              className={styles.search}
              placeholder="Buscar por nombre o email…"
              value={txQ}
              onChange={(e) => {
                setTxQ(e.target.value);
                setTxPage(0);
              }}
            />
            <div className={styles.chipRow}>
              <Chip on={!txKind} onClick={() => { setTxKind(""); setTxPage(0); }}>Todos {n(txCharges.length)}</Chip>
              {["renewal", "first", "lifetime"].map((k) => (
                <Chip key={k} on={txKind === k} color={KIND[k].color} onClick={() => { setTxKind(txKind === k ? "" : k); setTxPage(0); }}>
                  {KIND[k].label} {n(txCharges.filter((c) => c.kind === k).length)}
                </Chip>
              ))}
              <Chip on={txPlatform === "APPLE"} onClick={() => { setTxPlatform(txPlatform === "APPLE" ? "" : "APPLE"); setTxPage(0); }}>
                <FaApple /> {n(txCharges.filter((c) => c.r.platform === "APPLE").length)}
              </Chip>
              <Chip on={txPlatform === "GOOGLE"} onClick={() => { setTxPlatform(txPlatform === "GOOGLE" ? "" : "GOOGLE"); setTxPage(0); }}>
                <FaAndroid color="#3DDC84" /> {n(txCharges.filter((c) => c.r.platform === "GOOGLE").length)}
              </Chip>
            </div>
            <span className={styles.muted}>{n(txFiltered.length)} {txFiltered.length === 1 ? "cobro" : "cobros"}</span>
          </div>
          <div className={styles.list}>
            {txPageRows.length === 0 && <div className={styles.empty}>Sin cobros con estos filtros</div>}
            {txPageRows.map((c) => (
              <ChargeRow key={c.id} c={c} onOpen={openCustomer} />
            ))}
          </div>
          {txPages > 1 && (
            <div className={styles.pager}>
              <button type="button" disabled={txPage === 0} onClick={() => setTxPage(txPage - 1)}>‹ Anterior</button>
              <span>Página {txPage + 1} de {txPages}</span>
              <button type="button" disabled={txPage + 1 >= txPages} onClick={() => setTxPage(txPage + 1)}>Siguiente ›</button>
            </div>
          )}
          <div className={styles.footNote}>Cobros reconstruidos de los periodos cubiertos · € con el precio real de Apple (pasa el ratón por el importe para verlo en su moneda) y de referencia en Google</div>
        </Card>
      )}

      {/* ════ CLIENTES ════ */}
      {tab === "clientes" && (
        <Card className={styles.card} title="Clientes" classTitle="title-green" head={<button type="button" className={styles.exportBtn} onClick={exportCsv}>Exportar CSV</button>}>
          <div className={styles.chipRow}>
            {SEGMENTS.map((s) => (
              <Chip key={s.key} on={segKey === s.key} color={s.color} onClick={() => chooseSegment(s.key)}>
                {s.title} {n(rows.filter((r) => s.states.includes(r.state)).length)}
              </Chip>
            ))}
          </div>
          {seg.explain && <p className={styles.explain}>{seg.explain}</p>}

          <div className={styles.toolbar}>
            <input
              className={styles.search}
              placeholder="Buscar por nombre o email…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
            {planKeys.length > 1 && (
              <select className={styles.select} value={plan} onChange={(e) => setF(setPlan)(e.target.value)}>
                <option value="">Plan: todos ({n(segRows.length)})</option>
                {planKeys.map((x) => (
                  <option key={x} value={x}>
                    {PLAN[x]} ({n(segRows.filter((r) => r.plan === x).length)})
                  </option>
                ))}
              </select>
            )}
            <select className={styles.select} value={platform} onChange={(e) => setF(setPlatform)(e.target.value)}>
              <option value="">Tienda: las dos</option>
              <option value="APPLE">iOS ({n(segRows.filter((r) => r.platform === "APPLE").length)})</option>
              <option value="GOOGLE">Android ({n(segRows.filter((r) => r.platform === "GOOGLE").length)})</option>
            </select>
            {seg.extra.length > 0 && (
              <select className={styles.select} value={extra} onChange={(e) => setF(setExtra)(e.target.value)}>
                <option value="">Estado: todos</option>
                {seg.extra.map((e2) => (
                  <option key={e2.key} value={e2.key}>
                    {e2.label} ({n(segRows.filter(e2.test).length)})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className={styles.whenHead}>
            <div className={styles.whenTitle}>{seg.when}</div>
            <div className={styles.legend}>
              {planKeys.map((p) => (
                <span key={p}><i style={{ background: PLAN_COLOR[p] }} /> {PLAN[p]}</span>
              ))}
            </div>
          </div>
          {when.buckets.length === 0 ? (
            <div className={styles.state}>Nadie en este grupo</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={when.buckets} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} style={{ cursor: "pointer" }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
                <XAxis dataKey="label" {...axisProps} minTickGap={8} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: darkMode.value ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}
                  content={({ active, payload }) => {
                    const b = payload?.[0]?.payload;
                    return active && b ? (
                      <div className={styles.tooltip}>
                        <strong>{b.label}: {b.total} {b.total === 1 ? "persona" : "personas"}</strong>
                        {planKeys.map((p) => (b[p] ? <div key={p}>{PLAN[p]}: {b[p]}</div> : null))}
                      </div>
                    ) : null;
                  }}
                />
                {planKeys.map((p, i) => (
                  <Bar key={p} dataKey={p} stackId="a" fill={PLAN_COLOR[p]} maxBarSize={28} radius={i === planKeys.length - 1 ? [6, 6, 0, 0] : 0} onClick={(d) => pickBucket(d?.payload ?? d)}>
                    {when.buckets.map((b) => (
                      <Cell key={b.key} fill={PLAN_COLOR[p]} fillOpacity={!bucket || bucket.key === b.key ? 1 : 0.3} />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}

          <div className={styles.listHead}>
            {bucket && (
              <Chip on onClick={() => setBucket(null)}>
                Solo {bucket.label} ✕
              </Chip>
            )}
            <span className={styles.muted}>{n(list.length)} {list.length === 1 ? "persona" : "personas"} · clic en una fila para ver su ficha</span>
          </div>

          <div className={styles.listCols}>
            <span />
            <span>Cliente</span>
            <span>Plan</span>
            <button type="button" className={cn(styles.colBtn, { [styles.colOn]: orden === "recientes" || orden === "antiguos" })} onClick={() => headSort("fecha")}>
              {seg.dateLabel} {orden === "antiguos" ? "▲" : orden === "recientes" ? "▼" : ""}
            </button>
            <span>Estado</span>
            <button type="button" className={cn(styles.colBtn, { [styles.colOn]: orden === "ltv" })} onClick={() => headSort("ltv")}>
              Pagado {orden === "ltv" ? "▼" : ""}
            </button>
            <button type="button" className={cn(styles.colBtn, { [styles.colOn]: orden === "juega" })} onClick={() => headSort("juega")}>
              Juega 30d {orden === "juega" ? "▼" : ""}
            </button>
          </div>
          <div className={styles.list}>
            {pageRows.length === 0 && <div className={styles.empty}>Nadie con estos filtros</div>}
            {pageRows.map((r) => (
              <PersonRow key={r._id} r={r} date={seg.date(r)} onOpen={openCustomer} />
            ))}
          </div>
          {pages > 1 && (
            <div className={styles.pager}>
              <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Anterior</button>
              <span>Página {page + 1} de {pages}</span>
              <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Siguiente ›</button>
            </div>
          )}
        </Card>
      )}

      {/* ════ MODAL ════ */}
      <Modal visible={!!modal} onClose={() => setModal(null)} outerClassName={styles.modalOuter}>
        {modal && modal.kind === "customer" && cust && (
          <div>
            {modal.back && (
              <button type="button" className={styles.linkBtn} onClick={() => setModal(modal.back)}>
                ‹ Volver
              </button>
            )}
            <div className={styles.custHead}>
              <Avatar user={cust.user} size="lg" />
              <div className={styles.who}>
                <div className={styles.custName}>{cust.user?.name || cust.user?.email || "Sin usuario vinculado"}</div>
                <div className={styles.email}>{cust.user?.name ? cust.user?.email : ""}</div>
                <div className={styles.custBadges}>
                  <span className={styles.badge} style={{ color: (STATE[cust.state] || STATE.ended).color, background: (STATE[cust.state] || STATE.ended).bg }}>
                    {(STATE[cust.state] || STATE.ended).label}
                  </span>
                  <span className={styles.badge} style={{ color: PLAN_COLOR[cust.plan], background: "rgba(154,159,165,.12)" }}>
                    <PlatformIcon platform={cust.platform} size={11} /> {PLAN[cust.plan]}
                  </span>
                </div>
              </div>
              {cust.user?.email && (
                <Link className={styles.linkBtn} to={`/users?search=${encodeURIComponent(cust.user.email)}`}>
                  Ficha en Usuarios ›
                </Link>
              )}
            </div>
            <div className={styles.custStats}>
              <div><span className={styles.muted}>Pagado</span><strong>{ltvOf(cust) ? `≈ ${eur(ltvOf(cust))}` : "—"}</strong></div>
              <div><span className={styles.muted}>Cobros</span><strong>{n((cust.payments ?? []).length)}</strong></div>
              <div>
                <span className={styles.muted}>{cust.paid && !cust.refunded ? "Cliente desde" : "Alta"}</span>
                <strong>{dayShort(cust.paid && !cust.refunded ? cust.firstPaymentAt || cust.activatedAt : cust.activatedAt)}</strong>
              </div>
              <div><span className={styles.muted}>Partidas 30d</span><strong>{n(cust.user?.games30d ?? 0)}</strong></div>
              <div><span className={styles.muted}>Partidas total</span><strong>{n(cust.user?.gamesTotal ?? 0)}</strong></div>
            </div>
            <div className={styles.modalList}>
              <Timeline
                items={[
                  ...custTl.ev.map((e) => ({
                    color: (EVENT[e.kind] || EVENT.end).color,
                    date: day(e.t),
                    sub: relPast(e.t),
                    what: (
                      <>
                        {e.label}
                        {e.eur ? <span className={styles.muted}> · ≈ {eur(e.eur)}</span> : ""}
                      </>
                    ),
                  })),
                  ...(custTl.next ? [{ next: true, date: day(custTl.next.t), sub: relDay(custTl.next.t), what: custTl.next.label }] : []),
                ]}
              />
            </div>
          </div>
        )}
        {modal && modal.kind !== "customer" && (
          <div>
            <div className={styles.modalTitle}>{modal.title}</div>
            <div className={styles.modalSub}>
              {n(modal.items.length)} {modal.kind === "charges" ? (modal.items.length === 1 ? "cobro" : "cobros") : modal.items.length === 1 ? "persona" : "personas"}
              {modal.subtitle ? ` · ${modal.subtitle}` : ""}
            </div>
            <div className={styles.modalList}>
              {modal.items.length === 0 && <div className={styles.empty}>{modal.kind === "charges" ? "Ningún cobro" : "Nadie"}</div>}
              {modal.kind === "charges"
                ? modal.items.map((c) => <ChargeRow key={c.id} c={c} onOpen={openCustomer} />)
                : modal.items.map((r) => (
                    <PersonRow key={r._id} r={r} date={modal.dateOf ? modal.dateOf(r) : r.activatedAt} dateMode={modal.dateMode || "past"} onOpen={openCustomer} />
                  ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Subscriptions;
